import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FaSyncAlt, FaSort, FaSortUp, FaSortDown, FaSearch, FaTimes } from 'react-icons/fa';
import CommonHeader from '../../util_components/common_header';
import Paginator from '../../util_components/paginator';
import DateRangeFilter from '../early_retention_checklist/DateRangeFilter';
import MultiSelectDropdown from '../early_retention_checklist/MultiSelectDropdown';
import { get_api_url } from '../../../utils/urls';
import { get_api, post_api } from '../../../redux/api_funcs';
import { image_error_replace } from '../../../util_functions';
import { getAuthUserInfoOrDefault } from '../../../myt_chat/network/cookie';
import { CallbackHandler } from '../../../myt_chat/chat/callbacks/handler';
import * as actions from '../../../redux/action_creators';
import rule_config_line from './rule_line';
import is_overdue, { is_terminal } from './overdue';
import {
  elapsed_ms,
  format_duration_minutes,
  format_elapsed,
  first_standard_index,
  is_instant_live,
  short_label,
  timer_color_css
} from './instant';
import { write_persisted_filters } from './filters_storage';
import build_gen_config_payload from './gen_config';
import rule_notes_lines from './rule_notes';
import {
  blank_expiry_default_minutes,
  delay_label,
  delay_title,
  enabled_since_label,
  expiry_placeholder,
  expiry_title
} from './rule_card_labels';
import InstantReport from './InstantReport';
import TabPanel from './TabPanel';
import { resolveTab, searchWithTab, tabForKey, tabList } from './page_tabs';
import {
  STATUS_FILTERS,
  TYPE_OPTIONS,
  UNASSIGNED_VALUE,
  filter_query_params,
  initial_filter_state
} from './filters';

// How long to wait after the last keystroke before asking the server for the
// searched page. Long enough that typing a name is one request, short enough
// that the list feels like it is tracking what you type.
const SEARCH_DEBOUNCE_MS = 300;

// Rows open in a separate tab now ("Open Task" is target="_blank"), so tasks get
// completed or skipped while this tab sits untouched and its list goes stale.
// Coming back to the tab refetches — but only if the data is at least this old,
// so alt-tabbing back and forth does not turn into a request per switch.
const FOCUS_REFRESH_STALE_MS = 5000;

// Trailing debounce for the fresh-lead realtime refetch, so a burst of relay
// packets (several signups landing in one sweep pass) collapses into a single
// list request. Mirrors the CRM listener on /reminders.
const RELAY_REFRESH_DEBOUNCE_MS = 500;

// How often the instant rows' elapsed timers advance. One second because the
// timer is rendered as HH:MM:SS — anything slower and the seconds column would
// visibly skip.
const TIMER_TICK_MS = 1000;

// Sales task list — one row per (student, sales task). Filters mirror the
// early-retention checklist: date range, assignee and task-type dropdowns,
// status tabs, plus a type-to-search box on the student's first name.
class SalesTaskList extends Component {
  // The logged-in cms_user's uuid (from the ops JWT) — used to seed the
  // "Assigned to" filter and matched against each task's assigned_uuid.
  // Declared before `state` because class fields initialise in source order and
  // the initial filter reads it.
  current_cms_uuid = getAuthUserInfoOrDefault().uuid || '';

  state = {
    items: [],
    // Total rows matching the current filters (server-reported), for the
    // pagination controls. items holds only the current page.
    total: 0,
    // Assignee dropdown options ([{uuid, name}]) and whether any unassigned task
    // exists — both come from the list response, independent of the page/filters.
    assignees: [],
    has_unassigned: false,
    loading: false,
    // The four persisted filters (status, type, assignee, due-date range) plus
    // the date-range "touched" flag. Restored from localStorage when present,
    // otherwise seeded with the defaults — unassigned + self, and a rolling
    // yesterday→tomorrow (IST) window. Resolved here, before the first request,
    // so the initial load already carries the right filters and there is no
    // second round-trip.
    ...initial_filter_state(this.current_cms_uuid),
    // Type-to-search on the student's first name. Deliberately NOT part of the
    // persisted filter set: a search is a momentary "where is X", not a working
    // preference, and restoring one on the next visit would look like an empty
    // task list. Resolved server-side (see search_timer / set_search).
    search: '',
    gen_rules: [],
    gen_min_day: 0,
    gen_max_day: 29,
    // Whether THIS user may edit the generation rules (server-reported; only a
    // SUPER_USER may). Everyone can see the section either way — non-super-users
    // get it read-only. Defaults to false so the controls are never briefly
    // live before the config response lands.
    gen_can_edit: false,
    // Bounds for the per-rule "refresh every N hours" / "due in N hours"
    // inputs (server-supplied; these are only the pre-load fallbacks).
    gen_min_refresh_hours: 0,
    gen_max_refresh_hours: 720,
    gen_min_due_offset_hours: 0,
    gen_max_due_offset_hours: 720,
    // Bounds for the per-rule instant "expiry after N minutes" / "dedup window
    // of N hours" inputs (server-supplied; these are only the pre-load
    // fallbacks, matching MinExpiryMinutes/MaxExpiryMinutes and
    // MinDedupWindowHours/MaxDedupWindowHours).
    gen_min_expiry_minutes: 1,
    gen_max_expiry_minutes: 10080,
    gen_min_dedup_hours: 0,
    gen_max_dedup_hours: 720,
    // Bounds for the per-rule "grace of N minutes" input, shown on the rules
    // that declare has_grace (today only LEAD_OTP_NOT_VERIFIED). Matches
    // MinGraceMinutes/MaxGraceMinutes.
    gen_min_grace_minutes: 0,
    gen_max_grace_minutes: 1440,
    // Bounds for the per-rule "delay of N minutes" input, shown on the rules
    // that declare has_delay (the delayed-check friction rules). Matches
    // MinDelayMinutes/MaxDelayMinutes.
    gen_min_delay_minutes: 1,
    gen_max_delay_minutes: 1440,
    // Server-supplied coverage notes for the Task Generation card (rule_notes
    // on the generation_config GET). Null until the config lands.
    gen_rule_notes: null,
    saving_config: false,
    config_dirty: false,
    generating: false,
    sort_key: 'due_datetime',
    sort_dir: 'asc',
    page: 1,
    // Bumped once a second so the instant rows' elapsed timers re-render. Held
    // in state (not on the instance) precisely because it must cause a render;
    // nothing reads its value.
    tick: 0
  };

  page_size = 20;

  // Pending debounce for the search box (see set_search). Held on the instance
  // rather than in state — it drives no rendering, and touching state per
  // keystroke would re-render the whole table for nothing.
  search_timer = null;

  // When the last /list response landed, and whether one is in flight. Both feed
  // refresh_on_focus only — they drive no rendering, so they stay off state.
  last_loaded_at = 0;

  loading_in_flight = false;

  // Pending debounce for the fresh-lead relay refetch (see on_relay_item).
  // Instance field for the same reason as search_timer: it drives no rendering.
  relay_refresh_timer = null;

  // Per-second interval driving state.tick (see TIMER_TICK_MS).
  tick_timer = null;

  componentDidMount() {
    this.load_data();
    this.load_gen_config();
    this.tick_timer = setInterval(
      () => this.setState((prev) => ({ tick: prev.tick + 1 })),
      TIMER_TICK_MS
    );
    window.addEventListener('focus', this.refresh_on_focus);
    document.addEventListener('visibilitychange', this.refresh_on_focus);
    CallbackHandler.shared.registerRelayItemListener(this.on_relay_item);
  }

  componentWillUnmount() {
    // A debounce that fires after unmount would setState on a dead component.
    if (this.search_timer) clearTimeout(this.search_timer);
    if (this.relay_refresh_timer) clearTimeout(this.relay_refresh_timer);
    if (this.tick_timer) clearInterval(this.tick_timer);
    window.removeEventListener('focus', this.refresh_on_focus);
    document.removeEventListener('visibilitychange', this.refresh_on_focus);
    CallbackHandler.shared.unregisterRelayItemListener(this.on_relay_item);
  }

  // A fresh-lead relay means a FRESH_LEAD row was just inserted server-side, so
  // the open list is stale the moment the packet lands. Refetch with the current
  // filters — trailing-debounced so a burst of signups is one request. The app
  // shell (home/index.jsx) owns the desktop notification for the same packet;
  // this listener only keeps the visible table live.
  on_relay_item = (item) => {
    if (!item || !item.data || item.data.type !== 'fresh_lead') return;
    if (this.relay_refresh_timer) clearTimeout(this.relay_refresh_timer);
    this.relay_refresh_timer = setTimeout(() => {
      this.relay_refresh_timer = null;
      this.load_data();
    }, RELAY_REFRESH_DEBOUNCE_MS);
  };

  // Refetch when the agent returns to this tab, because the task they just worked
  // in the other tab is no longer pending. Deliberately cheap: nothing happens
  // while the tab is hidden, while a request is already running, or while the
  // current data is still fresh — the two listeners overlap (a tab switch fires
  // both), and the staleness guard is what keeps that to one request.
  refresh_on_focus = () => {
    if (document.visibilityState === 'hidden') return;
    if (this.loading_in_flight) return;
    if (Date.now() - this.last_loaded_at < FOCUS_REFRESH_STALE_MS) return;
    // A pending search debounce is about to reload with a newer term anyway.
    if (this.search_timer) return;
    this.load_data();
  };

  // Persist the four filters whenever any of them changes. Driven off state
  // rather than wired into each setter so a future filter setter can't forget to
  // save. agent_filter is replaced (never mutated) by set_agent_filter, so the
  // identity comparison is sound.
  componentDidUpdate(prev_props, prev_state) {
    const watched = [
      'status_filter',
      'type_filter',
      'agent_filter',
      'date_from',
      'date_to',
      'date_range_touched'
    ];
    if (watched.some((k) => prev_state[k] !== this.state[k])) this.persist_filters();
  }

  persist_filters = () => {
    const { status_filter, type_filter, agent_filter, date_from, date_to, date_range_touched } =
      this.state;
    write_persisted_filters({
      status_filter,
      type_filter,
      agent_filter,
      // null = still the rolling default, so it re-seeds from today next time.
      date_range: date_range_touched ? { from: date_from, to: date_to } : null
    });
  };

  load_gen_config = () => {
    const url = get_api_url('/v2/sales_task_list/generation_config');
    get_api(url, true)
      .then((res) => {
        const d = res.data.data || {};
        this.setState({
          gen_rules: d.rules || [],
          // Server-reported edit permission. The save/run endpoints enforce it
          // themselves (403 for a non-super-user), so this only decides whether
          // the controls are live — it is not the gate.
          gen_can_edit: !!d.can_edit,
          gen_min_day: d.min_day || 0,
          gen_max_day: d.max_day || 29,
          gen_min_refresh_hours: d.min_refresh_hours ?? 0,
          gen_max_refresh_hours: d.max_refresh_hours ?? 720,
          gen_min_due_offset_hours: d.min_due_offset_hrs ?? 0,
          gen_max_due_offset_hours: d.max_due_offset_hrs ?? 720,
          gen_min_expiry_minutes: d.min_expiry_minutes ?? 1,
          gen_max_expiry_minutes: d.max_expiry_minutes ?? 10080,
          gen_min_dedup_hours: d.min_dedup_hours ?? 0,
          gen_max_dedup_hours: d.max_dedup_hours ?? 720,
          gen_min_grace_minutes: d.min_grace_minutes ?? 0,
          gen_max_grace_minutes: d.max_grace_minutes ?? 1440,
          gen_min_delay_minutes: d.min_delay_minutes ?? 1,
          gen_max_delay_minutes: d.max_delay_minutes ?? 1440,
          // Product-owned sentences about PRD triggers that are covered by an
          // existing rule instead of a card of their own. Served with the
          // config so the wording lives in one repo; a server that does not
          // send them renders no note at all.
          gen_rule_notes: d.rule_notes || null,
          config_dirty: false
        });
      })
      .catch(() => {
        /* config is optional context; failure is non-blocking */
      });
  };

  set_rule = (task_type, patch) => {
    this.setState((prev) => ({
      gen_rules: prev.gen_rules.map((r) => (r.task_type === task_type ? { ...r, ...patch } : r)),
      config_dirty: true
    }));
  };

  // Numeric hour inputs (refresh rate / due offset). Kept as a number and
  // clamped to the server-supplied bounds; a cleared input reads as the min.
  set_rule_hours = (task_type, field, raw, min, max) => {
    let n = parseInt(raw, 10);
    if (Number.isNaN(n)) n = min;
    n = Math.min(Math.max(n, min), max);
    this.set_rule(task_type, { [field]: n });
  };

  // Optional numeric inputs (instant expiry minutes / dedup window hours).
  // Unlike set_rule_hours, a cleared box stays cleared instead of snapping to
  // the minimum: blank is a meaningful value for both fields (no expiry / fall
  // back to the server default), and snapping expiry up to its 1-minute
  // minimum would silently give the rule a one-minute lifetime.
  set_rule_optional = (task_type, field, raw, min, max) => {
    if (raw === '' || raw === null || raw === undefined) {
      this.set_rule(task_type, { [field]: '' });
      return;
    }
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return;
    this.set_rule(task_type, { [field]: Math.min(Math.max(n, min), max) });
  };

  // Select-all checkbox: tick = every day slot, untick = none.
  toggle_all_rule_days = (task_type, select_all) => {
    const { gen_min_day, gen_max_day } = this.state;
    const days = [];
    if (select_all) {
      for (let d = gen_min_day; d <= gen_max_day; d += 1) days.push(d);
    }
    this.set_rule(task_type, { days });
  };

  // Toggle one day chip in a rule's day set (kept sorted for display).
  toggle_rule_day = (task_type, day) => {
    this.setState((prev) => ({
      gen_rules: prev.gen_rules.map((r) => {
        if (r.task_type !== task_type) return r;
        const days = (r.days || []).includes(day)
          ? (r.days || []).filter((d) => d !== day)
          : [...(r.days || []), day].sort((a, b) => a - b);
        return { ...r, days };
      }),
      config_dirty: true
    }));
  };

  // Persist all generation rules (explicit Save).
  save_gen_config = () => {
    const rules = build_gen_config_payload(this.state.gen_rules);
    this.setState({ saving_config: true });
    const url = get_api_url('/v2/sales_task_list/generation_config');
    post_api(url, { config: { rules } }, true)
      .then(() => {
        this.props.set_notification_variable(true, 'success', 'Generation config saved');
        this.setState({ saving_config: false });
        this.load_gen_config();
      })
      .catch((e) => {
        let err = 'Failed to save config';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
        this.setState({ saving_config: false });
      });
  };

  // Manually kick the generator (runs async server-side, ignoring the per-rule
  // refresh rates).
  run_generate = () => {
    this.setState({ generating: true });
    const url = get_api_url('/v2/sales_task_list/run_generate');
    post_api(url, {}, true)
      .then(() => {
        this.props.set_notification_variable(
          true,
          'success',
          'Generation started — refresh the list in a minute'
        );
      })
      .catch((e) => {
        let err = 'Failed to start generation';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
      })
      .finally(() => this.setState({ generating: false }));
  };

  // Build the /list query string from the current filter/sort/page state.
  // Filtering, sorting and pagination are all server-side now, so paging is
  // correct over the full table (not a capped client-side window).
  //
  // The four filters are serialised by the shared helper (filters.js), which the
  // task form's "Done and Next" also uses so both walk the same set.
  build_query = () => {
    const { sort_key, sort_dir } = this.state;
    const params = filter_query_params(this.state);
    params.set('page', String(this.state.page));
    params.set('limit', String(this.page_size));
    params.set('sort_key', sort_key);
    params.set('sort_dir', sort_dir);
    return params.toString();
  };

  load_data = () => {
    this.loading_in_flight = true;
    this.setState({ loading: true });
    const url = get_api_url(`/v2/sales_task_list/list?${this.build_query()}`);
    get_api(url, true)
      .then((res) => {
        const d = res.data.data || {};
        const items = d.items || [];
        const assignees = d.assignees || [];
        // No post-load filter defaulting: the "Assigned to" default (unassigned +
        // self) is seeded in the initial state, so the first request already
        // carries it and there is no second round-trip — and a user who clears or
        // changes the filter is never overridden by a later response.
        this.setState({
          items,
          total: typeof d.total === 'number' ? d.total : items.length,
          assignees,
          has_unassigned: !!d.has_unassigned,
          loading: false
        });
      })
      .catch((e) => {
        let err = 'Failed to load sales tasks';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
        this.setState({ loading: false });
      })
      .finally(() => {
        // Stamped on failure too: a focus loop that retried a failing endpoint
        // every switch would be worse than showing stale rows.
        this.loading_in_flight = false;
        this.last_loaded_at = Date.now();
      });
  };

  // Href for a task's detail view. Rendered as a real <Link>/<a> with
  // target="_blank" (see render_row) rather than a history.push handler so the
  // list survives the agent working a task: the queue they were browsing stays
  // put, scrolled and filtered, in this tab. Being an anchor also means
  // cmd/ctrl-click, middle-click and "Open in new window" behave natively —
  // window.open in an onClick would break all three and trip popup blockers.
  //
  // ?from=list is kept: it is what makes the form render its "Back to list" link
  // and send the list's filters with "Done and Next" (see form.jsx).
  //
  // The four persisted filters reach the form through localStorage, which crosses
  // tabs; the search term deliberately does NOT persist (see `search` in state),
  // so it would be invisible to a form opened in a new tab and "Done and Next"
  // would walk outside the searched set. It therefore rides along in the href —
  // but only when a search is actually active, so an unsearched list keeps the
  // short, clean link it had before.
  form_link = (item) => {
    const { search: raw_search } = this.state;
    const search = (raw_search || '').trim();
    const suffix = search ? `&search=${encodeURIComponent(search)}` : '';
    return `/sales-task-form?uuid=${encodeURIComponent(item.uuid)}&from=list${suffix}`;
  };

  // Reload the current page from the server. Used as the setState callback of
  // every filter/sort/page setter (all filtering is server-side now).
  reload = () => this.load_data();

  set_page = (page) => this.setState({ page }, this.reload);

  // Filter changes reset to the first page so the user isn't stranded on an
  // out-of-range page, then reload from the server.
  set_status_filter = (status_filter) => this.setState({ status_filter, page: 1 }, this.reload);

  // Receives the full next selection ([] = all types) and replaces it, never
  // mutates — which is what makes componentDidUpdate's identity check sound.
  set_type_filter = (type_filter) => this.setState({ type_filter, page: 1 }, this.reload);

  // Picking or clearing the range marks it touched, which is what makes it
  // persist as absolute dates instead of re-seeding as the rolling default.
  set_date_range = ({ from, to }) =>
    this.setState({ date_from: from, date_to: to, date_range_touched: true, page: 1 }, this.reload);

  clear_date_range = () =>
    this.setState({ date_from: '', date_to: '', date_range_touched: true, page: 1 }, this.reload);

  // An empty selection means "all assignees" — clearing is a real choice and is
  // never re-seeded with the default.
  set_agent_filter = (agent_filter) => this.setState({ agent_filter, page: 1 }, this.reload);

  // Type-to-search on the student's first name. The input is controlled, so the
  // typed value lands in state immediately (no lag while typing) but the request
  // is debounced: a burst of keystrokes fires ONE list call, not one per letter.
  //
  // Like every other filter this resets to page 1 — a search that kept the
  // current page would land the agent on an out-of-range page of a smaller
  // result set and look empty.
  set_search = (search) => {
    if (this.search_timer) clearTimeout(this.search_timer);
    this.setState({ search, page: 1 });
    this.search_timer = setTimeout(() => {
      this.search_timer = null;
      this.reload();
    }, SEARCH_DEBOUNCE_MS);
  };

  // Clearing is immediate — an explicit "×" is a decision, not typing, so there
  // is nothing to wait for.
  clear_search = () => {
    if (this.search_timer) clearTimeout(this.search_timer);
    this.search_timer = null;
    this.setState({ search: '', page: 1 }, this.reload);
  };

  // "Assigned to" options come from the list response's assignee list, plus an
  // "Unassigned" option (tasks with no assignee) when the server reports any.
  //
  // Both default selections are also force-included even when the response has
  // not arrived (or does not list them): the dropdown renders a selected value
  // with no matching option as its raw uuid, and the seeded default would flash
  // "__unassigned__, <uuid>" on first paint otherwise.
  agent_options = () => {
    const { assignees, has_unassigned, agent_filter } = this.state;
    const options = (assignees || [])
      .map((a) => ({ value: a.uuid, label: a.name || a.uuid }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const self = this.current_cms_uuid;
    if (self && agent_filter.includes(self) && !options.some((o) => o.value === self)) {
      options.unshift({ value: self, label: 'Me' });
    }
    if (has_unassigned || agent_filter.includes(UNASSIGNED_VALUE)) {
      options.unshift({ value: UNASSIGNED_VALUE, label: 'Unassigned' });
    }
    return options;
  };

  // Click a sortable column header: toggle direction if same key, else asc,
  // then reload the (re-sorted) first page from the server.
  toggle_sort = (sort_key) =>
    this.setState(
      (prev) => ({
        sort_key,
        sort_dir: prev.sort_key === sort_key && prev.sort_dir === 'asc' ? 'desc' : 'asc',
        page: 1
      }),
      this.reload
    );

  // The instant marker on a list row. A fact about the task, so it shows on
  // terminal instant rows too — it is how an agent looking at an auto-completed
  // row understands why it expired.
  instant_pill = (item) => (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-xs font-bold tracking-wide bg-state-warning-bg text-state-warning-text whitespace-nowrap"
      title={short_label(item.task_type, item.task_label)}
    >
      INSTANT
    </span>
  );

  // Age of a live instant task, coloured by the PRD thresholds (black under ten
  // minutes, amber to thirty, red past it). Re-rendered every second by
  // state.tick. Returns null when the row has no parseable creation instant, so
  // the due cell falls back to the timestamp rather than showing 00:00:00.
  render_elapsed = (item) => {
    const ms = elapsed_ms(item.created_at_iso, Date.now());
    if (ms === null) return null;
    return (
      <span
        style={{
          color: timer_color_css(ms),
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        ⏱ {format_elapsed(ms)} elapsed
      </span>
    );
  };

  // Task cell: the same rule-config line as the form's header —
  // "[Rule name] - Day N - 17 july 2026" (shared helper in rule_line.js).
  task_badge = (item) => (
    <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-state-info-bg text-state-info-text whitespace-nowrap">
      {rule_config_line(
        item.task_label || item.task_type,
        item.rule_day,
        item.created_date_iso,
        item.task_type
      )}
    </span>
  );

  status_badge = (status) => {
    const map = {
      draft: { cls: 'bg-state-warning-bg text-state-warning-text', label: 'Draft' },
      submitted: { cls: 'bg-state-success-bg text-state-success-text', label: 'Submitted' },
      pending: { cls: 'bg-surface-sunken text-content-secondary', label: 'Pending' },
      // Terminal, set by the system rather than by an agent: the student became a
      // member, so their open tasks were closed automatically (goserver's
      // student_became_member lifecycle hook). These rows are excluded from the
      // Pending / Overdue / Submitted tabs server-side and only surface under
      // "All", which is where an agent goes to find out where a task went.
      auto_closed: { cls: 'bg-surface-sunken text-content-muted', label: 'Auto closed' },
      // Terminal, set by the instant-expiry cron: an instant task nobody touched
      // within its rule's expiry window. Deliberately NOT folded into
      // "Auto closed" — that one means the reason for the task went away, this
      // one means nobody got to it in time, and reporting must not merge them.
      auto_completed: {
        cls: 'bg-surface-sunken text-content-muted',
        label: 'Auto completed'
      }
    };
    const s = map[status] || map.pending;
    return (
      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
        {s.label}
      </span>
    );
  };

  // Clickable, sortable column header. Always shows a sort affordance: a muted
  // double-arrow when inactive, an amber up/down when it's the active sort.
  render_sort_th = (label, key) => {
    const active = this.state.sort_key === key;
    const asc = this.state.sort_dir === 'asc';
    let Icon = FaSort;
    let color = 'var(--text-muted)';
    if (active) {
      Icon = asc ? FaSortUp : FaSortDown;
      color = 'var(--state-warning)';
    }
    return (
      <th
        className="text-left px-5 py-2 font-medium cursor-pointer select-none hover:bg-surface-sunken transition-colors"
        onClick={() => this.toggle_sort(key)}
        title="Sort"
      >
        <span className="inline-flex items-center gap-1.5">
          <span style={active ? { color: 'var(--state-warning)' } : undefined}>{label}</span>
          <Icon size={12} color={color} style={{ verticalAlign: 'middle' }} />
        </span>
      </th>
    );
  };

  // Type-to-search box, sized and coloured to sit level with the
  // MultiSelectDropdown triggers beside it (same padding, radius, font size and
  // input tokens) — it is one more control in the filter row, not a banner
  // above it. Border turns brand-coloured while a term is active, matching how
  // the dropdowns signal "this filter is doing something".
  render_search_box = () => {
    const { search } = this.state;
    const active = !!search;
    return (
      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
        <FaSearch
          size={12}
          color="var(--text-muted)"
          style={{ position: 'absolute', left: 10, pointerEvents: 'none' }}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => this.set_search(e.target.value)}
          // Escape clears — the same reflex as any other search field, and
          // faster than reaching for the × with the hand already on the keys.
          onKeyDown={(e) => {
            if (e.key === 'Escape' && search) this.clear_search();
          }}
          aria-label="Search tasks by student first name"
          placeholder="Search first name"
          style={{
            width: 200,
            padding: '6px 26px 6px 28px',
            borderRadius: 8,
            fontSize: 13,
            border: '1px solid',
            borderColor: active ? 'var(--brand-primary)' : 'var(--input-border)',
            background: 'var(--input-bg)',
            color: 'var(--input-text)',
            margin: 0
          }}
        />
        {active ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear search"
            title="Clear"
            onClick={this.clear_search}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') this.clear_search();
            }}
            style={{
              position: 'absolute',
              right: 8,
              display: 'inline-flex',
              cursor: 'pointer',
              color: 'var(--text-muted)'
            }}
          >
            <FaTimes size={11} />
          </span>
        ) : null}
      </div>
    );
  };

  // Dark segmented tabs (same style as the early-retention checklist filters).
  render_segmented = (options, selected, on_select) => (
    <div className="inline-flex rounded-full" style={{ background: '#1e293b', padding: 2 }}>
      {options.map((o) => {
        const is_sel = selected === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => on_select(o.value)}
            style={{
              padding: '2px 10px',
              fontSize: 11,
              fontWeight: 600,
              border: 'none',
              borderRadius: 9999,
              cursor: 'pointer',
              background: is_sel ? '#ffffff' : 'transparent',
              color: is_sel ? '#1e293b' : '#cbd5e1'
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );

  // Numeric input shared by the two instant-tier fields, styled exactly like the
  // refresh-rate / due-offset boxes above so the card reads as one control set.
  render_instant_number = ({
    rule,
    field,
    value,
    disabled,
    min,
    max,
    suffix,
    title,
    label,
    placeholder = ''
  }) => (
    <label
      className="inline-flex items-center gap-2 text-xs"
      style={{ opacity: disabled ? 0.5 : 1, color: 'var(--text-muted)' }}
      title={title}
    >
      {label}
      <input
        type="number"
        disabled={disabled}
        min={min}
        max={max}
        value={value}
        placeholder={placeholder}
        onChange={(e) => this.set_rule_optional(rule.task_type, field, e.target.value, min, max)}
        style={{
          width: 64,
          padding: '2px 6px',
          borderRadius: 6,
          border: '1px solid var(--input-border)',
          background: 'var(--input-bg)',
          color: 'var(--input-text)'
        }}
      />
      {suffix}
    </label>
  );

  // Instant tier, on EVERY rule card including the enabled-only ones: an instant
  // rule's tasks sort above every standard task and expire if nobody touches
  // them. The two numeric fields only appear once the toggle is on — they mean
  // nothing for a standard rule, and showing four inert boxes on every card
  // would bury the day chips that are the card's actual subject.
  render_instant_controls = (rule, locked) => {
    const {
      gen_min_expiry_minutes,
      gen_max_expiry_minutes,
      gen_min_dedup_hours,
      gen_max_dedup_hours,
      gen_min_grace_minutes,
      gen_max_grace_minutes,
      gen_min_delay_minutes,
      gen_max_delay_minutes
    } = this.state;
    const disabled = locked || !rule.enabled;
    // The server sends 0 for "no expiry"; the box is blank in both cases.
    const expiry = rule.expiry_minutes ? rule.expiry_minutes : '';
    const effective_expiry = expiry || blank_expiry_default_minutes(rule.task_type);
    const dedup =
      rule.dedup_window_hours === '' ||
      rule.dedup_window_hours === null ||
      rule.dedup_window_hours === undefined
        ? ''
        : rule.dedup_window_hours;
    // 0 is a real grace ("act the moment the lead is created"), so only a truly
    // absent value blanks the box.
    const grace =
      rule.grace_minutes === '' || rule.grace_minutes === null || rule.grace_minutes === undefined
        ? ''
        : rule.grace_minutes;
    // Unlike grace, 0 is not a real delay: the server's minimum is 1 and would
    // clamp it up, so a server 0 means "no opinion" and blanks the box.
    const delay = rule.delay_minutes ? rule.delay_minutes : '';
    return (
      <>
        <label
          className="inline-flex items-center gap-2 text-xs font-medium"
          style={{
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            color: 'var(--text-muted)'
          }}
          title="Instant tasks sort above every standard task and expire if untouched."
        >
          <input
            type="checkbox"
            disabled={disabled}
            checked={!!rule.is_instant}
            onChange={(e) => this.set_rule(rule.task_type, { is_instant: e.target.checked })}
          />
          Instant
        </label>
        {rule.is_instant
          ? this.render_instant_number({
              rule,
              field: 'expiry_minutes',
              value: expiry,
              disabled,
              min: gen_min_expiry_minutes,
              max: gen_max_expiry_minutes,
              label: 'Expiry duration',
              suffix: 'minutes',
              title: expiry_title(rule.task_type),
              placeholder: expiry_placeholder(rule.task_type)
            })
          : null}
        {rule.is_instant
          ? this.render_instant_number({
              rule,
              field: 'dedup_window_hours',
              value: dedup,
              disabled,
              min: gen_min_dedup_hours,
              max: gen_max_dedup_hours,
              label: 'Dedup window',
              suffix: 'hours',
              title:
                'A second task of this type for the same member is suppressed inside this window. 0 = no window.'
            })
          : null}
        {/* Grace is orthogonal to the instant tier — it delays task CREATION,
            not expiry — so it is gated on the server's has_grace, not on the
            Instant toggle. */}
        {rule.has_grace
          ? this.render_instant_number({
              rule,
              field: 'grace_minutes',
              value: grace,
              disabled,
              min: gen_min_grace_minutes,
              max: gen_max_grace_minutes,
              label: 'Grace',
              suffix: 'minutes',
              title:
                'How long to wait after the trigger before creating the task, giving the member a chance to finish on their own. Blank = the server default.'
            })
          : null}
        {/* Delay is the wait before the delayed re-check that decides whether
            the friction resolved itself, so like grace it gates on the
            server's has_delay rather than on the Instant toggle. */}
        {rule.has_delay
          ? this.render_instant_number({
              rule,
              field: 'delay_minutes',
              value: delay,
              disabled,
              min: gen_min_delay_minutes,
              max: gen_max_delay_minutes,
              label: delay_label(rule.task_type),
              suffix: null,
              title: delay_title(rule.task_type)
            })
          : null}
        {rule.is_instant && effective_expiry ? (
          <span
            className="text-xs"
            style={{ color: 'var(--text-muted)', opacity: disabled ? 0.5 : 1 }}
          >
            untouched after {format_duration_minutes(effective_expiry)} → auto-completed
          </span>
        ) : null}
      </>
    );
  };

  render_enabled_since = (rule) => {
    const text = enabled_since_label(rule.enabled_at);
    if (!text) return null;
    return (
      <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
        {text}
      </div>
    );
  };

  // One card per generation rule: enable checkbox + toggleable day chips
  // (0..29) counting from the rule's anchor (signup date or concierge-bucket
  // entry date). A rule fires once per selected day — e.g. days 0, 2 and 5.
  // Below the chips: how often the rule re-scans for candidates, and how far
  // ahead of generation time the tasks it creates are due.
  render_rule_card = (rule) => {
    const {
      gen_min_day,
      gen_max_day,
      saving_config,
      gen_min_refresh_hours,
      gen_max_refresh_hours,
      gen_min_due_offset_hours,
      gen_max_due_offset_hours,
      gen_can_edit
    } = this.state;
    const all_days = [];
    for (let d = gen_min_day; d <= gen_max_day; d += 1) all_days.push(d);
    const selected = rule.days || [];
    // A rule whose generator never reads rule.days (CHURNED_MEMBER_LOGIN
    // re-scans on its own refresh window instead) says so with has_days:false.
    // Absent means true, so every rule the server has not classified yet keeps
    // the chips it has today.
    const has_days = rule.has_days !== false;
    // Non-super-users see every value exactly as a super user does, but cannot
    // change any of it: the whole card is inert rather than hidden, because the
    // point of opening this section is to find out what the generator is doing.
    const locked = saving_config || !gen_can_edit;
    const disabled = locked || !rule.enabled;

    // Enabled-only rules (Fresh lead, Upcoming trial session) are triggered by
    // a student event and/or their own dedicated sweep, not by the day-offset
    // generator: there is nothing to schedule, so the card is just the enable
    // toggle plus the trigger description.
    if (rule.enabled_only) {
      return (
        <div key={rule.task_type} className="rounded-md border border-line px-4 py-2.5">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input
                type="checkbox"
                checked={!!rule.enabled}
                disabled={locked}
                onChange={(e) => this.set_rule(rule.task_type, { enabled: e.target.checked })}
              />
              {rule.label}
            </label>
            {this.render_instant_controls(rule, locked)}
            <span className="ml-auto text-xs text-content-muted">{rule.anchor_label}</span>
          </div>
          {this.render_enabled_since(rule)}
        </div>
      );
    }

    return (
      <div key={rule.task_type} className="rounded-md border border-line px-4 py-2.5">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input
              type="checkbox"
              checked={!!rule.enabled}
              disabled={locked}
              onChange={(e) => this.set_rule(rule.task_type, { enabled: e.target.checked })}
            />
            {rule.label}
          </label>
          {rule.bucket ? (
            <span
              className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-state-info-bg text-state-info-text"
              title="Only students currently in this concierge bucket"
            >
              {rule.bucket}
            </span>
          ) : null}
          <span className="ml-auto text-xs text-content-muted">
            {!has_days ? rule.anchor_label : null}
            {has_days && selected.length > 0
              ? `Day ${selected.join(', ')} — ${rule.anchor_label}`
              : null}
            {has_days && selected.length === 0 ? `Pick the days (${rule.anchor_label})` : null}
          </span>
        </div>
        {has_days ? (
          <div className="flex items-center gap-1 flex-wrap mt-2">
            <label
              className="inline-flex items-center gap-1.5 text-xs font-medium mr-2"
              style={{
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                color: 'var(--text-muted)'
              }}
              title="Select or clear every day slot"
            >
              <input
                type="checkbox"
                disabled={disabled}
                checked={selected.length === all_days.length && all_days.length > 0}
                onChange={(e) => this.toggle_all_rule_days(rule.task_type, e.target.checked)}
              />
              All days
            </label>
            {all_days.map((d) => {
              const is_sel = selected.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  disabled={disabled}
                  onClick={() => this.toggle_rule_day(rule.task_type, d)}
                  title={`Day ${d} ${rule.anchor_label}`}
                  style={{
                    minWidth: 30,
                    padding: '3px 0',
                    fontSize: 11,
                    fontWeight: 600,
                    borderRadius: 6,
                    cursor: disabled ? 'default' : 'pointer',
                    border: is_sel ? '1px solid #2563eb' : '1px solid var(--border-default)',
                    background: is_sel ? '#2563eb' : 'var(--surface-card)',
                    color: is_sel ? '#ffffff' : 'var(--text-muted)',
                    opacity: disabled ? 0.5 : 1
                  }}
                >
                  {d}
                </button>
              );
            })}
          </div>
        ) : null}
        <div className="flex items-center gap-6 flex-wrap mt-2.5">
          <label
            className="inline-flex items-center gap-2 text-xs"
            style={{ opacity: disabled ? 0.5 : 1, color: 'var(--text-muted)' }}
            title="How often the generator re-scans this rule for candidates. The cron runs hourly; 0 = every run."
          >
            Refresh every
            <input
              type="number"
              disabled={disabled}
              min={gen_min_refresh_hours}
              max={gen_max_refresh_hours}
              value={rule.refresh_rate_hours ?? 24}
              onChange={(e) =>
                this.set_rule_hours(
                  rule.task_type,
                  'refresh_rate_hours',
                  e.target.value,
                  gen_min_refresh_hours,
                  gen_max_refresh_hours
                )
              }
              style={{
                width: 64,
                padding: '2px 6px',
                borderRadius: 6,
                border: '1px solid var(--input-border)',
                background: 'var(--input-bg)',
                color: 'var(--input-text)'
              }}
            />
            hours
          </label>
          <label
            className="inline-flex items-center gap-2 text-xs"
            style={{ opacity: disabled ? 0.5 : 1, color: 'var(--text-muted)' }}
            title="A generated task's due time = the moment it was generated + this many hours."
          >
            Due
            <input
              type="number"
              disabled={disabled}
              min={gen_min_due_offset_hours}
              max={gen_max_due_offset_hours}
              value={rule.due_offset_hours ?? 24}
              onChange={(e) =>
                this.set_rule_hours(
                  rule.task_type,
                  'due_offset_hours',
                  e.target.value,
                  gen_min_due_offset_hours,
                  gen_max_due_offset_hours
                )
              }
              style={{
                width: 64,
                padding: '2px 6px',
                borderRadius: 6,
                border: '1px solid var(--input-border)',
                background: 'var(--input-bg)',
                color: 'var(--input-text)'
              }}
            />
            hours after generation
          </label>
        </div>
        <div className="flex items-center gap-6 flex-wrap mt-2.5">
          {this.render_instant_controls(rule, locked)}
        </div>
        {this.render_enabled_since(rule)}
      </div>
    );
  };

  // Task-generation rules: which of the rules run, how often they re-scan, and
  // on which day from their anchor date. Members, family-plan students and
  // #stop / #nomembership-flagged students are always skipped server-side.
  //
  // VISIBLE to every agent who opens the page, EDITABLE only by a SUPER_USER.
  // It used to be hidden outright from everyone else, which meant an agent
  // wondering why a task type had stopped appearing had no way to find out that
  // its rule was simply switched off. Read-only answers that; the editing gate
  // is unchanged in effect and is now enforced server-side (the save and
  // "run generation now" endpoints 403 a non-super-user) rather than by this
  // component declining to render.
  render_config_section = () => {
    const { gen_rules, saving_config, config_dirty, generating, gen_can_edit, gen_rule_notes } =
      this.state;
    if (!gen_rules.length) {
      return (
        <div className="px-5 py-10 text-center text-sm text-content-muted">
          Task generation rules are not available yet.
        </div>
      );
    }
    return (
      <section
        className="bg-surface-card rounded-lg shadow-sm border border-line"
        style={{ margin: '20px 5%' }}
      >
        <div className="w-full px-5 py-3 flex items-center gap-2 border-b border-line">
          <h2 className="font-semibold">Task generation</h2>
          <span className="text-xs text-content-muted">
            Which sales tasks the generator creates, on which day, how often each rule re-scans and
            when its tasks fall due. Members, family-plan students and do-not-contact students are
            always skipped.
          </span>
          {gen_can_edit ? null : (
            <span className="text-xs font-medium text-content-muted whitespace-nowrap">
              View only — super users can edit
            </span>
          )}
          {config_dirty ? (
            <span className="text-xs font-medium text-amber-600">Unsaved changes</span>
          ) : null}
        </div>
        {/* Some PRD triggers have no rule card of their own because an
                existing rule already covers them. The sentences come from the
                server so the wording is not duplicated across two repos. */}
        {rule_notes_lines(gen_rule_notes).length > 0 && (
          <p
            className="px-5 pt-4 text-xs font-semibold"
            style={{ color: 'var(--text-muted)', margin: 0 }}
          >
            PRD triggers covered by an existing rule
          </p>
        )}
        {rule_notes_lines(gen_rule_notes).map((note) => (
          <p
            key={note}
            className="px-5 pt-2 text-xs"
            style={{ color: 'var(--text-muted)', margin: 0 }}
          >
            {note}
          </p>
        ))}
        <div className="px-5 py-4 flex flex-col gap-2">{gen_rules.map(this.render_rule_card)}</div>
        <div className="px-5 pb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={this.save_gen_config}
            disabled={saving_config || !config_dirty || !gen_can_edit}
            title={gen_can_edit ? undefined : 'Only super users can edit the generation config'}
            className="px-4 py-2 rounded-md text-sm font-medium hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            // Inline background/color override the host CMS's global orange
            // `button` + `button:hover` rules (Tailwind classes can't beat them).
            style={{
              background: '#2563eb',
              border: '1px solid #2563eb',
              color: '#ffffff',
              margin: 0
            }}
          >
            {saving_config ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={this.run_generate}
            disabled={generating || config_dirty || !gen_can_edit}
            title={(() => {
              if (!gen_can_edit) return 'Only super users can run the generator';
              if (config_dirty) return 'Save the config first';
              return 'Run every enabled rule now, ignoring refresh rates';
            })()}
            className="px-4 py-2 rounded-md text-sm font-medium hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--border-default)',
              color: 'var(--text-primary)',
              margin: 0
            }}
          >
            {generating ? 'Starting…' : 'Run generation now'}
          </button>
          {config_dirty && !saving_config ? (
            <span className="text-xs text-content-muted">You have unsaved changes</span>
          ) : null}
        </div>
      </section>
    );
  };

  render_row = (item) => {
    // Terminal covers auto_closed too — those rows are done, so they get the
    // read-only "View" action and never the overdue highlight.
    const terminal = is_terminal(item);
    const overdue = is_overdue(item);
    const elapsed = is_instant_live(item) ? this.render_elapsed(item) : null;
    return (
      <tr
        key={item.uuid}
        className={`border-t border-line hover:bg-surface-sunken ${
          overdue ? 'bg-state-error-bg-subtle' : ''
        }`}
      >
        <td className="text-left px-5 py-3">
          <div className="flex items-center gap-3">
            <img
              src={item.profile_photo}
              onError={image_error_replace}
              alt=""
              className="h-9 w-9 rounded-full object-cover bg-surface-sunken"
            />
            {/* A lead task carries the LEAD uuid in student_uuid — there is no
                student row behind it — so the profile link must not be built
                from it. */}
            {item.student_uuid && !item.is_lead ? (
              <Link
                to={`/studentprofile?id=${encodeURIComponent(item.student_uuid)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-600 hover:underline"
              >
                {item.student_name || item.student_uuid}
              </Link>
            ) : (
              <span className="font-medium text-content-primary">{item.student_name || '—'}</span>
            )}
          </div>
        </td>
        <td className="text-left px-5 py-3">
          <div className="flex items-center gap-2 flex-wrap">
            {item.is_instant ? this.instant_pill(item) : null}
            {this.task_badge(item)}
          </div>
        </td>
        <td
          className={`text-left px-5 py-3 whitespace-nowrap ${
            overdue ? 'text-state-error font-medium' : 'text-content-secondary'
          }`}
        >
          {/* An instant task's deadline is its expiry, not its due timestamp,
              and the number an agent acts on is how long it has been waiting —
              so the age replaces the due time for as long as the task is live.
              Once it is terminal the timer is history and the timestamp returns. */}
          {elapsed || item.due_datetime || '—'}
        </td>
        <td className="text-left px-5 py-3">{this.status_badge(item.status)}</td>
        <td className="text-left px-5 py-3 whitespace-nowrap text-content-secondary">
          {item.assigned_name || '—'}
        </td>
        <td className="text-left px-5 py-3">
          <Link
            to={this.form_link(item)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-3 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            {terminal ? 'View' : 'Open Task'}
          </Link>
        </td>
      </tr>
    );
  };

  render_body = (rows) => {
    if (rows.length === 0) {
      return (
        <tr>
          <td colSpan={6} className="px-5 py-8 text-center text-content-muted">
            No sales tasks match these filters.
          </td>
        </tr>
      );
    }
    // Live instant rows are sorted server-side ahead of everything else, so the
    // point where the due-date order resumes is invisible without a marker.
    // Finished instant rows sort with the standard block. Only when the page
    // holds both kinds: a page of one kind has no boundary to mark.
    const first_standard = first_standard_index(rows);
    const show_separator = first_standard > 0;
    const body = [];
    rows.forEach((item, idx) => {
      if (show_separator && idx === first_standard) {
        body.push(
          <tr key="standard-tasks-separator">
            <td
              colSpan={6}
              className="px-5 py-1.5 text-xs font-semibold tracking-wide"
              style={{ background: 'var(--surface-sunken)', color: 'var(--text-muted)' }}
            >
              STANDARD TASKS — SORTED BY DUE DATE
            </td>
          </tr>
        );
      }
      body.push(this.render_row(item));
    });
    return body;
  };

  // The report tab needs gen_can_edit, which lands with the config GET, so
  // ?tab=report shows Tasks until then; the URL is left alone so it resolves.
  can_view_report = () => {
    const { gen_can_edit } = this.state;
    return gen_can_edit;
  };

  active_tab = () => {
    const { location } = this.props;
    return resolveTab(location?.search || '', this.can_view_report());
  };

  select_tab = (tab) => {
    const { location, history } = this.props;
    if (!location || !history) return;
    const search = searchWithTab(location.search || '', tab);
    if (search === (location.search || '')) return;
    history.replace({ pathname: location.pathname, search });
  };

  on_tab_key = (e) => {
    const tabs = tabList(this.can_view_report());
    const next = tabForKey(tabs, this.active_tab(), e.key);
    if (!next) return;
    e.preventDefault();
    this.select_tab(next);
    const el = document.getElementById(`sales-tab-${next}`);
    if (el) el.focus();
  };

  render_tab_strip = (active) => (
    <div
      role="tablist"
      aria-label="Sales task list sections"
      className="flex items-center gap-1 border-b border-line"
      style={{ margin: '20px 5% 0' }}
    >
      {tabList(this.can_view_report()).map((t) => {
        const selected = t.id === active;
        return (
          <button
            key={t.id}
            id={`sales-tab-${t.id}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={`sales-tabpanel-${t.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => this.select_tab(t.id)}
            onKeyDown={this.on_tab_key}
            className="px-4 py-2 text-sm font-medium transition-colors hover:bg-surface-sunken"
            // Inline overrides beat the host CMS's global `button {}` rule.
            style={{
              background: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${selected ? 'var(--brand-primary)' : 'transparent'}`,
              borderRadius: 0,
              margin: 0,
              marginBottom: -1,
              color: selected ? 'var(--text-primary)' : 'var(--text-muted)',
              cursor: 'pointer'
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );

  render() {
    const { loading, date_from, date_to, agent_filter, status_filter, type_filter, total } =
      this.state;
    const active = this.active_tab();
    // items is already the current server page; total drives the paginator.
    const paged_rows = this.state.items;
    const page_limit = Math.max(1, Math.ceil(total / this.page_size));
    const page = Math.min(this.state.page, page_limit);

    return (
      <div>
        <CommonHeader title="Sales Task List" />

        {this.render_tab_strip(active)}

        <TabPanel id="generation" active={active}>
          {active === 'generation' ? this.render_config_section() : null}
        </TabPanel>

        {/* Reporting is SUPER_USER-only (product decision Q14), gated on the
            same server-reported capability as the generation card's editing.
            Kept mounted once allowed so its range and results survive tab
            switches; it loads on first open. */}
        {this.can_view_report() ? (
          <TabPanel id="report" active={active}>
            <InstantReport can_view open={active === 'report'} />
          </TabPanel>
        ) : null}

        <TabPanel id="tasks" active={active}>
          <section
            className="bg-surface-card rounded-lg shadow-sm border border-line"
            style={{ margin: '20px 5%' }}
          >
            <div className="px-5 py-3 border-b border-line">
              <div className="flex items-center gap-3">
                <h2 className="font-semibold">Tasks</h2>
                <span className="text-xs text-content-muted">
                  {total} {total === 1 ? 'item' : 'items'}
                </span>
                <button
                  type="button"
                  onClick={this.load_data}
                  disabled={loading}
                  aria-label="Refresh sales tasks"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 28,
                    width: 28,
                    borderRadius: 9999,
                    border: '1px solid var(--border-default)',
                    background: 'var(--surface-card)',
                    cursor: loading ? 'wait' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                    padding: 0
                  }}
                >
                  <FaSyncAlt size={13} color="#f59e0b" className={loading ? 'animate-spin' : ''} />
                </button>
              </div>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                {this.render_search_box()}
                <DateRangeFilter
                  from={date_from}
                  to={date_to}
                  onChange={this.set_date_range}
                  onClear={this.clear_date_range}
                />
                <MultiSelectDropdown
                  label="Assigned to"
                  options={this.agent_options()}
                  selected={agent_filter}
                  onChange={this.set_agent_filter}
                  onClear={() => this.set_agent_filter([])}
                  placeholder="All assignees"
                />
                <MultiSelectDropdown
                  label="Task type"
                  options={TYPE_OPTIONS}
                  selected={type_filter}
                  onChange={this.set_type_filter}
                  onClear={() => this.set_type_filter([])}
                  placeholder="All types"
                  width={210}
                />
                {this.render_segmented(STATUS_FILTERS, status_filter, this.set_status_filter)}
              </div>
            </div>

            {loading && this.state.items.length === 0 ? (
              // Initial load: only the list area shows a placeholder; on
              // refreshes the table stays visible and the refresh icon spins.
              <div className="px-5 py-10 text-center text-sm text-content-muted">
                Loading sales tasks…
              </div>
            ) : (
              <div className="overflow-x-auto">
                {/* The server orders instant rows first, ahead of the due-date
                  sort and ahead of whichever column the agent sorted by — which
                  looks like the sort controls are broken unless it is said out
                  loud. Only shown when the page actually has an instant row, so
                  a queue with none is not told about a tier it cannot see. */}
                {paged_rows.some(is_instant_live) ? (
                  <div className="px-5 pt-3 text-xs text-content-muted">
                    Instant tasks always appear first, regardless of due date.
                  </div>
                ) : null}
                <table className="min-w-full text-sm">
                  <thead className="bg-surface-sunken text-content-secondary">
                    <tr>
                      {this.render_sort_th('Member', 'member')}
                      {/* Sorts by task_type server-side, which groups rows of the
                        same rule together (the labels share the types' order). */}
                      {this.render_sort_th('Task', 'task')}
                      {this.render_sort_th('Due', 'due_datetime')}
                      {this.render_sort_th('Status', 'status')}
                      {/* Sorts by agent name server-side; unassigned rows always
                        sort last. */}
                      {this.render_sort_th('Assigned To', 'assigned')}
                      {/* Action holds the Open Task / View button — no data to
                        sort by (its label just mirrors Status). */}
                      <th className="text-left px-5 py-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>{this.render_body(paged_rows)}</tbody>
                </table>
              </div>
            )}

            {page_limit > 1 ? (
              <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-line">
                <span className="text-xs text-content-muted">
                  Showing {(page - 1) * this.page_size + 1}–{Math.min(page * this.page_size, total)}{' '}
                  of {total}
                </span>
                <Paginator curr_page={page} page_limit={page_limit} pageChange={this.set_page} />
              </div>
            ) : null}
          </section>
        </TabPanel>
      </div>
    );
  }
}

SalesTaskList.propTypes = {
  set_notification_variable: PropTypes.func.isRequired,
  location: PropTypes.shape({ pathname: PropTypes.string, search: PropTypes.string }),
  history: PropTypes.shape({ replace: PropTypes.func })
};

SalesTaskList.defaultProps = {
  location: undefined,
  history: undefined
};

// No mapStateToProps: the page used to read state.home.access_type to decide
// whether to render the Task Generation section at all. That section is now
// visible to everyone and its editability comes from the config endpoint's
// `can_edit` (which the server derives from the same cms_user row it enforces
// the write gate with), so the redux copy of the role is no longer consulted.
const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

export default connect(null, mapDispatchToProps)(SalesTaskList);
